import { PureComponent } from 'react';
import type { FeatureCollection, Feature } from 'geojson';
import { layer, source, feature } from 'react-openlayers';

import EventBus from '../../../EventBus';
import { MISSION_EVENTS } from '../../../missionEvents';
import { getCurrentMission } from '../../../dataModelHelpers';
import { convertProjection4329 } from '../../../utils';

const Z_INDEX_DEBUG = -1; // Beneath PathLayer / RobotPathLayer.

interface Props {
  visible: boolean;
  // Show the cyan "dubins-tsp-cost" feature inside the debug GeoJSON. It
  // visualises the kinematic curves used by pathfinder's TSP cost model
  // between TSP-level cores — NOT the robot's actual driving plan (that's
  // the blue RobotPathLayer). Useful for cost-model debugging; gated to
  // Technician access by the parent.
  showDubinsTspCost: boolean;
  // Show the turquoise "dubins-final" feature: the dense polyline (0.5 m
  // discretisation) of the Dubin's chain pathfinder's DGP solver produced
  // for legs it resolved during reconstruct_paths. Distinct from the blue
  // RobotPathLayer (pathfinder's driven path) and the cyan TSP-cost overlay (LKH
  // cost-model curves). Empty for legs solved by direct/corridor.
  showDgpCurves: boolean;
  // Show the orange "dubins-direct" feature: the dense polyline of the
  // Dubin's curve the direct-Dubin's-first check produced for legs it
  // resolved. Most legs in well-shaped fields are solved here.
  showDirectDubins: boolean;
  // Show the lavender "hybrid-astar-final" feature: the dense polyline of
  // the Dubin's chain pathfinder's Hybrid A* solver produced for legs it
  // resolved (slotted between direct-Dubin's and DGP in the pipeline).
  // Empty for legs solved by direct, DGP, or corridor.
  showHybridAstarCurves: boolean;
}

interface State {
  geojson: FeatureCollection | null;
}

type Payload = { geojson: FeatureCollection; missionId: number } | null;

/**
 * Renders pathfinder's debug GeoJSON (CDT triangulation + final tour +
 * cyan TSP-cost Dubin's overlay) as a MapMaker layer.
 *
 * Activated by SmartPathForm dispatching MISSION_EVENTS.DEBUG_GEOJSON_UPDATED
 * with a payload. A null payload clears the buffered features. The `visible`
 * prop is an additional gate driven by a MapMaker layer toggle — when off,
 * the layer renders nothing even if features are buffered. `showDubinsTspCost`
 * controls whether the cyan TSP-cost overlay is included; the rest of the
 * features (CDT, blue tour) always render when `visible` is on.
 */
export default class DebugGeoJsonLayer extends PureComponent<Props, State> {
  state: State = { geojson: null };

  private handlePayload = (payload: Payload) => {
    const mission = getCurrentMission();

    if (!payload || !mission || payload.missionId !== mission.instance_id) {
      this.setState({ geojson: null });

      return;
    }

    this.setState({ geojson: payload.geojson });
  };

  private handleMissionSwap = () => this.handlePayload(null);

  componentDidMount() {
    EventBus.on(MISSION_EVENTS.DEBUG_GEOJSON_UPDATED, this.handlePayload);
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.handleMissionSwap);
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.DEBUG_GEOJSON_UPDATED, this.handlePayload);
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.handleMissionSwap);
  }

  private styleFor(f: Feature) {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    const type = (p.type as string) || '';

    if (type === 'dubins-tsp-cost') {
      return { stroke: '#FF1493', fill: 'rgba(0,0,0,0)', strokeWidth: 6, opacity: 0.9 };
    }

    if (type === 'dubins-final') {
      return { stroke: '#00CED1', fill: 'rgba(0,0,0,0)', strokeWidth: 4, opacity: 0.85 };
    }

    if (type === 'dubins-direct') {
      return { stroke: '#FFA500', fill: 'rgba(0,0,0,0)', strokeWidth: 3, opacity: 0.85 };
    }

    if (type === 'hybrid-astar-final') {
      // Lavender — distinct from DGP turquoise / direct orange / TSP pink.
      return { stroke: '#bf94e4', fill: 'rgba(0,0,0,0)', strokeWidth: 4, opacity: 0.85 };
    }

    if (type === 'final_path') {
      return { stroke: '#0000ff', fill: 'rgba(0,0,0,0)', strokeWidth: 3, opacity: 0.5 };
    }

    // Corridor (A*-picked) triangles take priority over walkable green so the
    // user can see exactly which faces A* selected for this tour.
    if (p.corridor === true) {
      return { stroke: '#000000', fill: 'rgba(51,153,255,0.45)', strokeWidth: 0.7 };
    }

    const walkable = p.walkable === true;

    return walkable
      ? { stroke: '#000000', fill: 'rgba(0,170,0,0.25)', strokeWidth: 0.5 }
      : { stroke: '#000000', fill: 'rgba(170,0,0,0.25)', strokeWidth: 0.5 };
  }

  private buildFeatures(fc: FeatureCollection): JSX.Element[] {
    const elements: JSX.Element[] = [];

    fc.features.forEach((f, idx) => {
      const featureType = ((f.properties ?? {}) as Record<string, unknown>).type as string;

      // The cyan TSP-cost overlay is gated by a Technician-only parent toggle.
      if (featureType === 'dubins-tsp-cost' && !this.props.showDubinsTspCost) {
        return;
      }

      if (featureType === 'dubins-final' && !this.props.showDgpCurves) {
        return;
      }

      if (featureType === 'dubins-direct' && !this.props.showDirectDubins) {
        return;
      }

      if (featureType === 'hybrid-astar-final' && !this.props.showHybridAstarCurves) {
        return;
      }

      const style = this.styleFor(f);

      if (f.geometry.type === 'LineString') {
        // Mirror: feature.LineStringReact + convertProjection4329, same as RobotPathLayer / PathLayer.
        const coordinates = f.geometry.coordinates.map((coord) => convertProjection4329([coord[0], coord[1]]));
        elements.push(
          <feature.LineStringReact
            key={`debug-line-${idx}`}
            coordinates={coordinates}
            strokeOptions={{ color: style.stroke, width: style.strokeWidth }}
            zIndex={Z_INDEX_DEBUG}
            forceRefresh={true}
          />,
        );
      } else if (f.geometry.type === 'Point') {
        // Point markers — currently only `steiner_waypoint` is emitted (a
        // bright magenta halo with bigger radius than the regular path
        // anchor so it sits as a visible ring AROUND the standard waypoint
        // dot rendered by PathLayer). Indicates a leg where the corridor
        // smoother emitted a Dubin's chain that would clip a hole and
        // pathfinder had to insert an additional anchor at a funnel vertex
        // to route around it.
        if (featureType === 'steiner_waypoint') {
          const coord = convertProjection4329([f.geometry.coordinates[0], f.geometry.coordinates[1]]);
          elements.push(
            <feature.PointReact
              key={`debug-point-${idx}`}
              coordinate={coord}
              circleOptions={{
                radius: 8,
                fillOptions: { color: 'rgba(204, 0, 204, 0.55)' },
                strokeOptions: { color: '#cc00cc', width: 2 },
              }}
              zIndex={Z_INDEX_DEBUG}
            />,
          );
        }
      } else if (f.geometry.type === 'Polygon') {
        // Mirror: feature.PolygonReact with rings array, same as RoutingBoundaryLayer / ZonesLayer.
        const rings = f.geometry.coordinates.map((ring) =>
          ring.map((coord) => convertProjection4329([coord[0], coord[1]])),
        );
        elements.push(
          <feature.PolygonReact
            key={`debug-poly-${idx}`}
            coordinates={rings}
            strokeOptions={{ color: style.stroke, width: style.strokeWidth }}
            fillOptions={{ color: style.fill }}
          />,
        );
      }
      // Other geometry types (MultiPolygon, etc.) are silently skipped.
    });

    return elements;
  }

  render() {
    if (!this.props.visible || !this.state.geojson) {
      return null;
    }

    const features = this.buildFeatures(this.state.geojson);

    if (features.length === 0) {
      return null;
    }

    return (
      // @ts-ignore - layer.Vector children typing
      <layer.VectorImage zIndex={Z_INDEX_DEBUG}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>{features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
